import { isNullish } from '@sas/nova-core/typeguards';

export type StringTuple = Array<[string, string]>;

/**
 * A collection of functions useful when working with arrays
 * Many of these were ported over from dm-commons-ui
 * (https://gitlab.sas.com/dmv-gerrit/dm-commons-ui/-/blob/ae973a05d946490a94713c23629af7580584965a/src/main/typescript/sas/dm/commons/util/ArrayUtils.ts)
 */

/**
 * finds the item's index in the array using the given equality test
 * @param array
 * @param toFind
 * @param equalityTest function that takes two objects and returns true if they should be considered equal, false otherwise
 * @param startIndex (optional)
 * @returns {number}
 */
export const indexOfEqualityTest = (
  array: Array<any>,
  toFind: any,
  equalityTest: (item1: any, item2: any) => boolean,
  startIndex = 0
) => {
  if (!array) {
    return -1;
  }
  let startix = 0;
  if (startIndex < array.length && startIndex >= 0) {
    startix = startIndex;
  }
  const arrayLength = array.length;
  let counter = 0;
  while (counter < arrayLength) {
    let i = counter + startix;
    if (i > arrayLength - 1) {
      i = i - arrayLength;
    }
    if (equalityTest(array[i], toFind)) {
      return i;
    }
    counter++;
  }
  return -1;
};

/**
 * returns an array of the unique values from the given array. The first item in the array is used in the output array
 * when a duplicate is found.
 * @param array - the array to filter down to unique values
 * @param property - optional, when provided uses the value of the givent property of the object in the array to
 * determine equality between the elements on the array.  Should be null when equalityTest is specified
 * @param equalityTest - optional, when provided is called to determine the equality between two objects within the
 * array.  The function should take two items and return a boolean indicating if the items are equivalent
 * @returns Array<any> - an array containing the unique values from the input array.
 */
export const uniqueValues = (
  array: Array<any>,
  property?: string,
  equalityTest?: (item1: any, item2: any) => boolean
): Array<any> => {
  if (!array) {
    return array;
  }
  const ret: Array<any> = [];
  const arrayLength: number = array.length;
  let eqTest: ((item1: any, item2: any) => boolean) | undefined = equalityTest;
  if (!eqTest && property) {
    eqTest = (item1: any, item2: any): boolean => {
      return item1[property] === item2[property];
    };
  }

  if (!eqTest) {
    // brute force - referential equality check only
    for (let i = 0; i < arrayLength; i++) {
      const item: any = array[i];
      if (ret.indexOf(item) === -1) {
        ret.push(item);
      }
    }
  } else {
    for (let i = 0; i < arrayLength; i++) {
      const item: any = array[i];
      if (indexOfEqualityTest(ret, item, eqTest) === -1) {
        ret.push(item);
      }
    }
  }
  return ret;
};

export const tupleToRecord = <V>(tuple: Array<[string, V]>): Record<string, V> => {
  const record: Record<string, V> = {} as Record<string, V>;
  if (!tuple) {
    return record;
  }
  for (const value of tuple) {
    // we'll allow a nullish key
    record[value[0]] = value[1];
  }
  return record;
};

export const isEmpty = (arr: Array<any>): boolean => {
  if (isNullish(arr)) {
    return true;
  }
  return !(arr.hasOwnProperty('length') && arr.length > 0);
};

/**
 *
 * @param oldArray
 * @param newArray
 * @param useNewArrayOrder
 * @returns ArrayDiffResult compares two arrays returns an object with three key/value pairs:
 * ArrayDiffResult.added contains an array with all the objects that were added to newArray
 * ArrayDiffResult.common contains an array with all the objects that are in both oldArray and newArray
 * ArrayDiffResult.removed contains an array with all the objects that were removed from oldArray
 */
export const arrayDiff = <T>(
  oldArray: Array<T>,
  newArray: Array<T>,
  useNewArrayOrder = false
): ArrayDiffResult<T> => {
  const compared: ArrayDiffResult<T> = {} as ArrayDiffResult<T>;
  compared.added = diff(oldArray, newArray, byReferenceEquality);
  if (useNewArrayOrder) {
    compared.common = inCommon(newArray, oldArray, byReferenceEquality);
  } else {
    compared.common = inCommon(oldArray, newArray, byReferenceEquality);
  }
  compared.removed = diff(newArray, oldArray, byReferenceEquality);
  return compared;
};

/**
 *
 * @param arr1
 * @param arr2
 * @param equalityTest  to determine if two items should be considered "equal" the equalityTest parameter should be
 * a function that takes two objects and returns true if they should be considered equal.
 * @param returnItemsFromRight - if items are "equal" return the item from arr2 (optional)
 * @param matchCallback - callback that is fired when equivalent items are found (optional)
 * @returns Array<any> an array of items that are common between arr1 and arr2 using an equality test function
 */
export const inCommon = <T>(
  arr1: Array<T>,
  arr2: Array<T>,
  equalityTest: (item1: T, item2: T) => boolean,
  returnItemsFromRight = false,
  matchCallback?: (subject: T, subject2: T) => void
): Array<T> => {
  const common: Array<any> = [];
  if (isEmpty(arr1) || isEmpty(arr2)) {
    return common;
  }
  const arr1Copy: Array<any> = arr1.concat();
  const arr2Copy: Array<any> = arr2.concat();
  while (arr1Copy.length > 0) {
    const subject = arr1Copy.shift();
    if (isNullish(subject)) {
      continue;
    }
    const index = indexOfEqualityTest(arr2Copy, subject, equalityTest);
    if (index >= 0) {
      if (returnItemsFromRight) {
        common[common.length] = arr2Copy[index];
      } else {
        common[common.length] = subject;
      }
      if (!!matchCallback) {
        matchCallback(subject, arr2Copy[index]);
      }
      arr2Copy.splice(index, 1);
    }
  }
  return common;
};

/**
 * returns an array of the elements that are in arr2, but are not in arr1 using an equality test function
 * @param arr1
 * @param arr2
 * @param equalityTest (optional) to determine if two items should be considered "equal" the equalityTest parameter
 * should be a function that takes two objects and returns true if they should be considered equal.
 */
export const diff = <T>(
  arr1: Array<T>,
  arr2: Array<T>,
  equalityTest: (item1: T, item2: T) => boolean
): Array<T> => {
  if (!arr1 || arr1.length < 1) {
    return arr2;
  }
  const diffArr = [];
  if (!arr2) {
    arr2 = [];
  }
  const array2Length = arr2.length;
  for (let i = 0; i < array2Length; i++) {
    const item = arr2[i];
    if (isNullish(item)) {
      continue;
    }
    if (indexOfEqualityTest(arr1, item, equalityTest) < 0) {
      diffArr[diffArr.length] = item;
    }
  }
  return diffArr;
};

export interface ArrayDiffResult<T> {
  added: Array<T>;
  common: Array<T>;
  removed: Array<T>;
}

/**
 *
 * @param oldArray
 * @param newArray
 * @param equalityTest the equalityTest parameter should be a function that takes two objects and returns true if
 * they should be considered equal.
 * @param useNewArrayOrder an optional boolean, when true it will use the order of the items in newArray as the order of
 * the items returned in ArrayDiffResult.common
 * @param returnNewArrayEntries an optional boolean, when true uses the items from newArray to populate the array returned
 * by ArrayDiffResult.common
 * @param matchCallback  an optional function with signature function(oldArrayItem, newArrayItem) that will be called as a match
 * for each item is found
 * @returns ArrayDiffResult
 * ArrayDiffResult.added contains an array with all the objects that were added to newArray
 * ArrayDiffResult.common contains an array with all the objects that are in both oldArray and newArray
 * ArrayDiffResult.removed contains an array with all the objects that were removed from oldArray
 */
export const arrayDiffEqualityTest = <T>(
  oldArray: Array<T>,
  newArray: Array<T>,
  equalityTest: (item1: T, item2: T) => boolean,
  useNewArrayOrder = false,
  returnNewArrayEntries = false,
  matchCallback?: (subject: T, subject2: T) => void
): ArrayDiffResult<T> => {
  const compared: ArrayDiffResult<T> = {} as ArrayDiffResult<T>;
  compared.added = diff(oldArray, newArray, equalityTest);
  if (useNewArrayOrder) {
    // this will use the new array order but the old array's models
    let wrappedMatchCallback: ((a: any, b: any) => void) | undefined;
    if (!!matchCallback) {
      // wrap the callback but switch the order of the parameters since newArray is the subject going into InCommonEqualityTest
      wrappedMatchCallback = function (item1, item2) {
        matchCallback(item2, item1);
      };
    }
    if (returnNewArrayEntries) {
      compared.common = inCommon(newArray, oldArray, equalityTest, false, wrappedMatchCallback);
    } else {
      compared.common = inCommon(newArray, oldArray, equalityTest, true, wrappedMatchCallback);
    }
  } else {
    if (returnNewArrayEntries) {
      compared.common = inCommon(oldArray, newArray, equalityTest, true, matchCallback);
    } else {
      compared.common = inCommon(oldArray, newArray, equalityTest, false, matchCallback);
    }
  }
  compared.removed = diff(newArray, oldArray, equalityTest);
  return compared;
};

export const byReferenceEquality = <T>(a: T, b: T): boolean => a === b;
